import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal,
} from '@angular/core';
import { NotificationService } from '@et/notifications';
import { EjournalService } from '../../services/ejournal.service';
import { catchError, EMPTY, finalize } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import {
  JournalSealPdfPreviewComponent,
  JournalSealPdfPreviewData,
} from '../journal-seal-pdf-preview/journal-seal-pdf-preview.component';
import { PresentationalAtomsModule } from '@et/presentational/atoms';
import { EtJournalEntry, EtJournalESeal } from '@et/typings';

@Component({
  selector: 'et-journal-preview-pdf-btn',
  imports: [PresentationalAtomsModule],
  templateUrl: './preview-pdf-btn.component.html',
  styleUrl: './preview-pdf-btn.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex',
  },
})
export class PreviewPdfBtnComponent {
  private notificationService = inject(NotificationService);
  private ejournalService = inject(EjournalService);
  private dialog = inject(MatDialog);

  seal = input.required<EtJournalESeal | EtJournalEntry>();

  isLoadingPreview = signal(false);

  onPreviewDocument(): void {
    if (this.isLoadingPreview()) {
      return;
    }

    this.isLoadingPreview.set(true);
    this.ejournalService
      .getPacketPdfFile()
      .pipe(
        finalize(() => this.isLoadingPreview.set(false)),
        catchError(() => {
          this.notificationService.showError(
            'Failed to load document for preview',
          );
          return EMPTY;
        }),
      )
      .subscribe((file) => {
        const seal = this.seal();
        this.dialog.open<
          JournalSealPdfPreviewComponent,
          JournalSealPdfPreviewData
        >(JournalSealPdfPreviewComponent, {
          panelClass: ['et-dialog-radius', 'et-dialog-padding'],
          autoFocus: false,
          width: '95vw',
          height: '100%',
          maxWidth: '1200px',
          maxHeight: '95vh',
          data: {
            file,
            pageNumber: seal.pageNumber ?? 1,
            title: this.getTitle(seal),
          },
        });
      });
  }

  private getTitle(seal: EtJournalESeal | EtJournalEntry): string {
    if ('source' in seal) {
      return (
        seal.documentDescription ||
        (seal.source === 'eSeal'
          ? 'eSeal'
          : seal.source === 'deletedESeal'
          ? 'Deleted eSeal'
          : 'Seal')
      );
    }
    return seal.documentDescription || 'Entry';
  }
}
